/**
 * The country directory the Country Picker renders from.
 *
 * ===========================================================================
 * WHY THIS FILE EXISTS AT ALL — P2-T0 RESOLUTION C4
 * ===========================================================================
 * The picker has no reachable API. `GET /countries` requires `config.manage`,
 * which no AO or CD pilot Country Manager holds, so a builder who needed country
 * names would have hardcoded a code→name map with no provenance. Instead the
 * gate directed: source tile content from **the access token's `scopes`** (which
 * countries this user may enter) plus **spec §11** (what each country IS).
 *
 * §11 is transcribed in this repo at `apps/api/src/seeds/seed-countries.ts`, and
 * that file — not memory, not Figma — is the source for every DATA field below.
 * `countries.test.ts` asserts the key set equals `COUNTRY_CODES` from
 * `@crf/shared`, so this directory cannot silently drift out of the enum.
 *
 * ===========================================================================
 * FOUR RECORDED DIVERGENCES FROM FIGMA (node 37:6397). NONE IS AN INVENTION.
 * ===========================================================================
 * 1. **THERE IS NO CD TILE IN FIGMA**, and the fifth tile that would be CD is
 *    instead AO — a pilot country — used as the locked "No access" example. The
 *    design literally cannot depict either of the two pilot teams this phase
 *    ships for. CD is therefore authored here from §11 (`legal_entity = 'TBD'`,
 *    currency CDF, alt USD, tz Africa/Kinshasa, calendar FY) and the no-access
 *    state is driven by the token's scopes so that NO country is hardcoded as
 *    the locked example. See `CountryPickerPage`.
 *
 * 2. **LEGAL ENTITY NAMES DISAGREE, AND §11 WINS.** Figma writes fuller names —
 *    "Tally Global Software Trading LLC" (AE), "Tally Global Software (T)
 *    Limited" (TZ), "TALLYTCHI Global Prestação de Serviços (SU). LDA" (AO) —
 *    where §11 as seeded holds "TGS Trading LLC", "TGS (T) Ltd", "TALLYTCHI".
 *    §11 is used because `countries.legal_entity` is what the API holds and what
 *    documents will print; a picker that names the company differently from its
 *    own invoices is a lie in the UI, and the picker is effectively a view of
 *    that table. THE CONFLICT IS AN ESCALATION, NOT A CLOSED QUESTION: Figma's
 *    strings look like Master Report legal-table values, and the Master Report is
 *    not vendored into this repo, so neither side can be verified here. If the
 *    owner confirms Figma's names are the legal ones, the fix belongs in the
 *    SEED (one place), not in this file.
 *
 * 3. **CURRENCY CHIPS ARE ISO CODES, UNIFORMLY.** Figma renders "₹ INR" for IN
 *    but bare "AED"/"TZS" elsewhere, and "KZ" for Angola — which is the Kwanza's
 *    *symbol*, not its ISO code (§11 holds `AOA`). A symbol prefix authored for
 *    one country out of five is an inconsistency, and a symbol where the API
 *    holds a code is wrong data. All five chips show the ISO code from §11.
 *
 * 4. **THE TAX CHIP SHOWS THE LABEL, NOT A RATE.** Figma's IN chip reads
 *    "GST 18%"; 18% is not a country constant — rates live per product in
 *    `country_tax_rules` and, under owner decision D6, Tally is the tax master.
 *    Figma's AO chip reads "NIF [to be supplied]", but §11 has
 *    `tax_registration = NULL` for ALL FIVE countries (India's GSTIN included),
 *    so flagging only Angola would misrepresent the other four. Every chip
 *    therefore shows just the label §11 gives.
 */

/**
 * The scope codes, in Figma's tile order (IN, AE, TZ, AO), then CD, then XM.
 *
 * ⚠ `XM` IS NOT A COUNTRY — it is GS Multi-trade LLC, a UAE Free Zone entity that
 * sells where the group has no presence (§B3, owner ruling D-D). It lives here because
 * every scope in this system is keyed on a country code, and it sorts LAST because it
 * is the only entry that is not a place. Its code comes from ISO 3166's user-assigned
 * range (XA–XZ), which is never issued to a real country.
 */
export const COUNTRY_ORDER = ['IN', 'AE', 'TZ', 'AO', 'CD', 'XM'] as const;
export type CountryCode = (typeof COUNTRY_ORDER)[number];

export interface CountryProfile {
  readonly code: CountryCode;
  /** `countries.name` — §11. */
  readonly name: string;
  /** `countries.legal_entity` — §11. See divergence 2. */
  readonly legalEntity: string;
  /**
   * The city/region line under the entity name.
   *
   * Figma authored one for IN/AE/TZ/AO and this takes them verbatim: they are a
   * locality, not the registered `address_block` (which §11 holds as an explicit
   * "to be supplied" placeholder for every country, and which is what prints on
   * documents). CD, absent from Figma, follows Figma's OWN pattern for an unknown
   * address — its AO tile reads "Luanda, Angola [address to be supplied]" — with
   * the office city §11 pins via `tz = Africa/Kinshasa`.
   */
  readonly locality: string;
  /** `countries.currency` — §11. ISO code. See divergence 3. */
  readonly currency: string;
  /** `countries.alt_currency` — §11. Only CD has one (USD). */
  readonly altCurrency: string | null;
  /** Derived from `countries.fy_start_month` — §11. */
  readonly fiscalYear: string;
  /** `countries.tax_label` — §11. See divergence 4. */
  readonly taxLabel: string;
  /** `countries.tz` — §11 (IANA). */
  readonly tz: string;
  /**
   * `countries.saft_ao` — the fiscal-regime flag (migration 67, `docs/ANGOLA-AGT-VALIDATION-
   * DESIGN.md` §3 U0, owner "go" 2026-09-25): true where the country exports SAF-T(AO) and
   * signs under DE 74/19 — Angola, and only Angola (design §8). Mirrored from the API seed
   * and held to it by the parity test, like every other data field here. The Country
   * settings page keys its "Fiscal identity (AGT)" SECTION on the ROW's own `saft_ao`
   * (the server is the authority); this mirror is what the page can say before the row has
   * loaded, and it is never a substitute for the row.
   */
  readonly saftAo: boolean;
}

export const COUNTRIES: Readonly<Record<CountryCode, CountryProfile>> = {
  IN: {
    code: 'IN',
    name: 'India',
    legalEntity: 'Global Software',
    locality: 'Ashram Road, Ahmedabad 380009', // Figma I37:6573 (IN tile)
    currency: 'INR',
    altCurrency: null,
    fiscalYear: 'Apr–Mar FY', // fy_start_month = 4
    taxLabel: 'GSTIN',
    tz: 'Asia/Kolkata',
    saftAo: false,
  },
  AE: {
    code: 'AE',
    name: 'UAE',
    legalEntity: 'TGS Trading LLC',
    locality: 'Bur Dubai, Dubai, U.A.E.', // Figma I37:6655;29:49
    currency: 'AED',
    altCurrency: null,
    fiscalYear: 'Calendar year', // fy_start_month = 1
    taxLabel: 'TRN',
    tz: 'Asia/Dubai',
    saftAo: false,
  },
  TZ: {
    code: 'TZ',
    name: 'Tanzania',
    legalEntity: 'TGS (T) Ltd',
    locality: 'Azikiwe Street, Dar es Salaam, Tanzania', // Figma I37:6671;29:49
    currency: 'TZS',
    altCurrency: null,
    fiscalYear: 'Calendar year',
    taxLabel: 'VAT No.',
    tz: 'Africa/Dar_es_Salaam',
    saftAo: false,
  },
  AO: {
    code: 'AO',
    name: 'Angola',
    legalEntity: 'TALLYTCHI',
    locality: 'Luanda, Angola [address to be supplied]', // Figma I37:6687;29:86
    currency: 'AOA',
    altCurrency: null,
    fiscalYear: 'Calendar year',
    taxLabel: 'NIF',
    tz: 'Africa/Luanda',
    // The one regime row — design U0 / §8. Mirrors the API seed; the parity test reads it.
    saftAo: true,
  },
  CD: {
    // Authored from §11 because Figma has no CD tile. The legal entity was the
    // spec's 'TBD' placeholder until the owner confirmed the incorporated name on
    // 30 Jul 2026 (API seed commit 5b49017). The parity test in countries.test.ts
    // reads the API seed source directly, which is how the four days this mirror
    // lagged behind were caught — by a red suite, not by anyone remembering.
    code: 'CD',
    name: 'DR Congo',
    legalEntity: 'Tally Global Software RDC SARLU',
    locality: 'Kinshasa, DR Congo [address to be supplied]',
    currency: 'CDF',
    altCurrency: 'USD',
    fiscalYear: 'Calendar year',
    // EMPTY — owner ruling N-53, 2026-09-17: "there is no 'NIF' on Congolese documents in
    // this product." Mirrors the API seed (the parity test in countries.test.ts reads it);
    // an empty label shows no tax chip on the picker and no tax field on the party form.
    taxLabel: '',
    tz: 'Africa/Kinshasa',
    saftAo: false,
  },
  XM: {
    // GS Multi-trade LLC (§B3). No Figma tile exists and none should be invented: this
    // is authored from the API seed, which the parity test in countries.test.ts reads
    // directly. Address and TRN are owner-deferred placeholders, exactly as the seed
    // holds them — the locality line says so rather than implying a registered address.
    code: 'XM',
    name: 'GS Multi-trade',
    legalEntity: 'GS Multi-trade LLC',
    locality: 'UAE Free Zone [address to be supplied]',
    // USD, and it is the reason USD stopped being the FX_RATE_MISSING probe currency.
    currency: 'USD',
    altCurrency: null,
    fiscalYear: 'Calendar year',
    taxLabel: 'VAT',
    tz: 'Asia/Dubai',
    saftAo: false,
  },
};

export function isCountryCode(value: unknown): value is CountryCode {
  return typeof value === 'string' && (COUNTRY_ORDER as readonly string[]).includes(value);
}

export function countryProfile(code: CountryCode): CountryProfile {
  return COUNTRIES[code];
}

/**
 * The currency chip's text: the ISO code, plus the alternate where §11 records
 * one. Only CD has an alternate (USD), and showing it matters — a DRC user
 * quoting in USD needs to see that the company holds two currencies.
 */
export function currencyChip(profile: CountryProfile): string {
  return profile.altCurrency === null
    ? profile.currency
    : `${profile.currency} · ${profile.altCurrency}`;
}

/* ═══════════════════════════════════════════ §B3 — where the CUSTOMER is ══ */

/**
 * The label for a customer's own country, or `null` when there is nothing to say.
 *
 * ONE RENDERER, used by the party picker AND the quote record, because the two would
 * otherwise drift — and the whole point of the field is that a reader can tell two
 * customers apart, which fails the moment one screen states it and another does not.
 *
 * NULL IS THE COMMON CASE AND IS NOT A GAP. The five domestic scopes leave
 * `customer_country` unset: the customer is in the selling country by definition, and
 * printing "AO" beside an Angolan customer on an Angolan quote is noise that trains the
 * eye to skip the line — exactly when XM needs it read.
 *
 * The country NAME, not the raw code: "Kenya" is what a salesperson knows, and for a
 * code outside the five-plus-XM set (which `customer_country` explicitly permits — it is
 * any ISO-3166 alpha-2) the code itself is returned rather than a guess.
 */
export function customerOriginLabel(customerCountry: string | null | undefined): string | null {
  if (typeof customerCountry !== 'string') return null;
  const code = customerCountry.trim().toUpperCase();
  if (code === '') return null;
  const known = (COUNTRIES as Record<string, { name: string } | undefined>)[code];
  return `Customer in ${known?.name ?? code}`;
}
